import React, { useCallback, useEffect, useMemo, useState } from "react";
import API_BASE_URL from 'config/api';
import {
  Grid,
  CircularProgress,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Snackbar,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import DeleteIcon from "@mui/icons-material/Delete";
import MDBox from "components/MDBox";
import MDBadge from "components/MDBadge";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";
import { BlueButton, RedButton } from "styles";
import { useNavigate } from "react-router-dom";
import {
  fetchIntegrationReadiness,
  getReadinessBadge,
} from "services/integrationReadiness";

const USER_DATA_KEY = "userMeData";
const COOKIE_SUMMARY_KEY = "userCookieSummary";
const DEFAULT_CATEGORIES = [
  "necessary",
  "analytics",
  "marketing",
  "functionality",
  "uncategorized",
];

const Domains = () => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cookieSummary, setCookieSummary] = useState({});
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [integrationStatus, setIntegrationStatus] = useState({});
  const [scanNotice, setScanNotice] = useState("");
  const [deletingSiteId, setDeletingSiteId] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [siteToDelete, setSiteToDelete] = useState(null);
  const navigate = useNavigate();

  const getCachedUserData = () => {
    const cachedData = sessionStorage.getItem(USER_DATA_KEY);
    return cachedData ? JSON.parse(cachedData) : null;
  };

  const reloadUserData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/me`, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
        },
      });
      const data = await response.json();
      if (response.ok && data.user) {
        sessionStorage.setItem(USER_DATA_KEY, JSON.stringify(data));
        return data;
      }
      throw new Error("Kullanıcı verisi alınamadı");
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const loadCookieSummary = useCallback(async (tenantId) => {
    try {
      const cachedRaw = sessionStorage.getItem(`${COOKIE_SUMMARY_KEY}:${tenantId}`);
      const cachedData = cachedRaw ? JSON.parse(cachedRaw) : null;
      if (cachedData) {
        return cachedData;
      }

      const response = await fetch(
        `${API_BASE_URL}/cookie-definition/summary?tenantId=${tenantId}`,
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        sessionStorage.setItem(`${COOKIE_SUMMARY_KEY}:${tenantId}`, JSON.stringify(data));
        return data;
      }
      return {};
    } catch (err) {
      console.error("Çerez özeti yükleme hatası:", err);
      return {};
    }
  }, []);

  const determineCategories = (summaryData) => {
    const uniqueCategories = new Set(DEFAULT_CATEGORIES);

    Object.values(summaryData).forEach((siteSummary) => {
      Object.keys(siteSummary).forEach((category) => {
        if (category) {
          const lower = category.toLowerCase();
          if (lower !== "preferences") {
            uniqueCategories.add(lower);
          }
        }
      });
    });

    // uncategorized'ı sona al
    let categoriesArray = Array.from(uniqueCategories).filter(
      (c) => c !== "preferences"
    );
    const uncategorizedIndex = categoriesArray.indexOf("uncategorized");
    if (uncategorizedIndex > -1) {
      categoriesArray.splice(uncategorizedIndex, 1);
      categoriesArray.push("uncategorized");
    }

    return categoriesArray;
  };

  const handleDetailsClick = (siteId, defaultLanguage) => {
    navigate(`/cookies/categories?siteId=${siteId}&lang=${defaultLanguage}`);
  };

  const handleScanClick = async (siteId, requestDomain) => {
    try {
      const response = await fetch(`${API_BASE_URL}/scan/request`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
        },
        body: JSON.stringify({
          siteId,
          requestDomain,
        }),
      });

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(errText || "Tarama isteği başarısız");
      }

      setScanNotice("Tarama isteği başarıyla gönderildi.");
      const site = sites.find((item) => item.siteId === siteId);
      if (site) await refreshIntegrationForSite(site);
    } catch (error) {
      setError(error?.message || "Tarama sırasında hata oluştu");
    }
  };

  // Refactored: deletion logic is now handled via dialog
  const handleDeleteSite = async (siteId) => {
    try {
      setDeletingSiteId(siteId);
      const resp = await fetch(`${API_BASE_URL}/sites/${siteId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
        },
      });

      if (!resp.ok) {
        const errText = await resp.text().catch(() => "");
        throw new Error(errText || "Site silinemedi");
      }

      // Kullanıcı verisini güncelle (silinen siteyi listeden çıkar)
      setUserData((prev) => {
        if (!prev?.user?.clients) return prev;
        const next = {
          ...prev,
          user: {
            ...prev.user,
            clients: prev.user.clients.map((c) => ({
              ...c,
              sites: (c.sites || []).filter((s) => s.siteId !== siteId),
            })),
          },
        };
        try {
          sessionStorage.setItem(USER_DATA_KEY, JSON.stringify(next));
        } catch {}
        return next;
      });

      // sessionStorage içindeki userMeData keyini de güncelle
      const storedUserMeData = sessionStorage.getItem("userMeData");
      if (storedUserMeData) {
        try {
          const parsed = JSON.parse(storedUserMeData);
          if (parsed.user?.clients) {
            parsed.user.clients.forEach(client => {
              if (client.sites) {
                client.sites = client.sites.filter(site => site.siteId !== siteId);
              }
            });
          }
          sessionStorage.setItem("userMeData", JSON.stringify(parsed));
        } catch (e) {
          console.error("userMeData parse error", e);
        }
      }

      // Cookie summary'den silinen site verisini kaldır
      setCookieSummary((prev) => {
        const copy = { ...prev };
        try {
          delete copy[siteId];
        } catch {}
        try {
          (userData?.user?.clients || []).forEach((item) => {
            sessionStorage.removeItem(`${COOKIE_SUMMARY_KEY}:${item.clientId}`);
          });
        } catch {}
        return copy;
      });

      // Entegrasyon durumunu temizle
      setIntegrationStatus((prev) => {
        const copy = { ...prev };
        try {
          delete copy[siteId];
        } catch {}
        return copy;
      });

      setScanNotice("Site başarıyla silindi.");
    } catch (error) {
      setError(error?.message || "Site silinemedi");
    } finally {
      setDeletingSiteId(null);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        let user = getCachedUserData();
        if (!user) {
          user = await reloadUserData();
        }

        if (!user) return;

        setUserData(user);

        const clients = user.user?.clients || [];
        if (clients.length === 0) return;
        const summaries = await Promise.all(
          clients.map((item) => loadCookieSummary(item.clientId))
        );
        const summaryData = Object.assign({}, ...summaries);

        const newCategories = determineCategories(summaryData);
        setCategories(newCategories);

        setCookieSummary(summaryData);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [loadCookieSummary]);

  const clients = useMemo(() => userData?.user?.clients || [], [userData]);
  const sites = useMemo(() => clients.flatMap((item) =>
    (item.sites || []).map((site) => ({ ...site, clientId: item.clientId }))
  ), [clients]);

  const refreshIntegrationForSite = async (site) => {
    const token = sessionStorage.getItem("authToken");
    setIntegrationStatus((prev) => ({
      ...prev,
      [site.siteId]: { ...(prev[site.siteId] || {}), loading: true },
    }));
    try {
      const value = await fetchIntegrationReadiness(site.siteId, token);
      setIntegrationStatus((prev) => ({ ...prev, [site.siteId]: value }));
    } catch (e) {
      setIntegrationStatus((prev) => ({
        ...prev,
        [site.siteId]: { stage: "resolve_access", error: true },
      }));
    }
  };

  const checkIntegrations = useCallback(async () => {
    const token = sessionStorage.getItem("authToken");
    const statuses = {};
    for (const site of sites) {
      try {
        statuses[site.siteId] = await fetchIntegrationReadiness(site.siteId, token);
      } catch (e) {
        statuses[site.siteId] = { stage: "resolve_access", error: true };
      }
    }
    setIntegrationStatus(statuses);
  }, [sites]);

  useEffect(() => {
    if (!sites.length) return;
    checkIntegrations();
  }, [checkIntegrations, sites.length]);

  if (loading && !userData) {
    return (
      <DashboardLayout>
        <MDBox my={3} display="flex" justifyContent="center">
          <CircularProgress />
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <MDBox my={3} display="flex" justifyContent="center">
          <Typography color="error">{error}</Typography>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <MDBox mt={2} mb={4}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card sx={{ boxShadow: 3 }}>
              <CardContent>
                <Box
                  sx={{
                    mb: 1,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="h4">Domainler ve Çerezler</Typography>
                  <BlueButton
                    onClick={() => navigate("/company/account-purchase")}
                  >
                    Site Ekle
                  </BlueButton>
                </Box>
                <Typography variant="body1" color="text.secondary" paragraph>
                  Tüm sitelerinizdeki çerezleri kategorilerine göre
                  görüntüleyebilir ve yönetebilirsiniz.
                </Typography>

                {sites.length > 0 ? (
                  <Grid container spacing={3} sx={{ mt: 2 }}>
                    {sites.map((site) => {
                      const siteSummary = cookieSummary[site.siteId] || {};

                      return (
                        <Grid item xs={12} key={site.siteId}>
                          <Card sx={{ boxShadow: 2 }}>
                            <CardContent>
                              <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                mb={2}
                              >
                                <Box>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    <Typography variant="h5" fontWeight="bold">
                                      {site.domain}
                                    </Typography>
                                    <IconButton
                                      edge="end"
                                      aria-label="delete"
                                      // Instead of direct delete, open confirmation dialog
                                      onClick={() => {
                                        setSiteToDelete(site);
                                        setDeleteConfirmOpen(true);
                                      }}
                                      size="small"
                                      sx={{ color: "error.main" }}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                </Box>
                                <Box display="flex" alignItems="center">
                                  <Tooltip title="Bu site için entegrasyon durumunu yenile">
                                    <IconButton
                                      onClick={() =>
                                        refreshIntegrationForSite(site)
                                      }
                                      size="small"
                                      aria-label="Yenile"
                                      sx={{ mr: 1 }}
                                    >
                                      <RefreshIcon />
                                    </IconButton>
                                  </Tooltip>
                                  {(() => {
                                    const status = integrationStatus[site.siteId];
                                    const [badgeText, badgeColor] = !status || status?.loading
                                      ? ["Kontrol ediliyor", "warning"]
                                      : status?.error
                                      ? ["Erişim kontrolü gerekli", "error"]
                                      : getReadinessBadge(status);
                                    return (
                                      <MDBadge
                                        variant="contained"
                                        badgeContent={badgeText}
                                        color={badgeColor}
                                        container
                                      />
                                    );
                                  })()}
                                  {(() => {
                                    const status = integrationStatus[site.siteId];
                                    const canScan = status?.integration?.scanPrerequisitesMet;
                                    const disabledReason = !status
                                      ? "Entegrasyon durumu kontrol ediliyor"
                                      : status.stage === "configure_banner"
                                      ? "Dil/banner ayarları eksik"
                                      : status.stage === "resolve_access"
                                      ? "Site erişimi doğrulanmalı"
                                      : status.stage === "scan_in_progress"
                                      ? "Tarama devam ediyor"
                                      : "Tarama şu anda kullanılamıyor";
                                    return (
                                      <Tooltip title={!canScan ? disabledReason : ""}>
                                        <span>
                                          <BlueButton
                                            onClick={() => handleScanClick(site.siteId, site.domain)}
                                            size="small"
                                            sx={{ ml: 2, mr: 2 }}
                                            disabled={!canScan}
                                          >
                                            Çerezleri Tara
                                          </BlueButton>
                                        </span>
                                      </Tooltip>
                                    );
                                  })()}
                                  <Button
                                    variant="outlined"
                                    onClick={() => navigate(`/integrations/cmp-banner?siteId=${site.siteId}`)}
                                    size="small"
                                    sx={{ mr: 2 }}
                                  >
                                    Entegrasyon
                                  </Button>
                                  <BlueButton
                                    onClick={() =>
                                      handleDetailsClick(
                                        site.siteId,
                                        site.defaultLanguage
                                      )
                                    }
                                    size="small"
                                  >
                                    Detayları İncele
                                  </BlueButton>
                                </Box>
                              </Box>

                              <Typography
                                variant="body2"
                                color="text.secondary"
                                mb={2}
                              >
                                Varsayılan Dil:{" "}
                                {site.defaultLanguage === "en"
                                  ? "İngilizce"
                                  : "Türkçe"}
                              </Typography>

                              <Grid container spacing={2}>
                                {categories.map((category) => (
                                  <Grid
                                    item
                                    xs={6}
                                    sm={4}
                                    md={3}
                                    lg={2}
                                    key={category}
                                  >
                                    <Card
                                      variant="outlined"
                                      sx={{ p: 1.5, height: "100%" }}
                                    >
                                      <Typography
                                        variant="body2"
                                        fontWeight="medium"
                                        color="text.secondary"
                                      >
                                        {category.charAt(0).toUpperCase() +
                                          category.slice(1)}
                                      </Typography>
                                      <Typography variant="h5" mt={1}>
                                        {siteSummary[category] || 0}
                                      </Typography>
                                    </Card>
                                  </Grid>
                                ))}
                              </Grid>
                            </CardContent>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                ) : (
                  <Typography
                    variant="body1"
                    sx={{ mt: 2, textAlign: "center", py: 4 }}
                  >
                    Henüz bir site eklenmemiş
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Footer />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => !deletingSiteId && setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Siteyi Sil</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
            Site ile bağlı olan çerez onay geçmişiniz, üyeliğiniz, fatura
            bilgileriniz, çerez ve banner ayarlarınız da kalıcı olarak
            silinecektir. Bu işlem geri alınamaz.
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Silmek istediğinize emin misiniz?
          </Typography>
        </DialogContent>
        <DialogActions>
          <BlueButton
            onClick={() => setDeleteConfirmOpen(false)}
            disabled={!!deletingSiteId}
          >
            Hayır
          </BlueButton>
          <RedButton
            onClick={async () => {
              if (siteToDelete) {
                setDeletingSiteId(siteToDelete.siteId);
                await handleDeleteSite(siteToDelete.siteId);
                setDeleteConfirmOpen(false);
                setSiteToDelete(null);
              }
            }}
            color="error"
            variant="contained"
            disabled={!!deletingSiteId}
          >
            {deletingSiteId ? "İşleniyor..." : "Evet, Sil"}
          </RedButton>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!scanNotice}
        autoHideDuration={3500}
        onClose={() => setScanNotice("")}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity="success"
          onClose={() => setScanNotice("")}
          sx={{ width: "100%" }}
        >
          {scanNotice}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
};

export default Domains;
